/**
 * Layout — App-Shell-Wrapper. Rendert Sidebar + main(Topbar + page-container).
 *
 * Eingeführt in Slice-1 Redesign (2026-05-08), erweitert in Slice-3
 * (2026-05-09) um Mobile-Drawer-State + Backdrop + Esc/matchMedia/auto-close-
 * on-nav effects + Scroll-Lock + Focus-Refs.
 *
 * Mobile-Strategy (Slice 3):
 *  - Sidebar bleibt always-mounted (CSS-Switch via Tailwind, kein Mount-
 *    Switching) — SelectionContext + LayoutContext bleiben stable bei
 *    Resize/Tablet-Rotation. Pattern aus tailwind.md § Responsive-Pairs.
 *  - <md (Mobile): Sidebar position-fixed mit -translate-x-full / translate-x-0,
 *    Backdrop sibling rendert wenn open, body+main scroll-lock.
 *  - ≥md (Desktop): Sidebar position-static, Backdrop hidden.
 *  - matchMedia-Listener: on viewport ≥md → setSidebarOpen(false). Pattern
 *    aus tailwind.md § Body-Scroll-Lock-Sync.
 *  - Esc-Window-handler im Layout: Drawer schließt. Dialog-Guard skipt nur
 *    [role="dialog"]/[role="alertdialog"]. Menus regulieren sich via
 *    stopPropagation (siehe AvatarMenu). Pattern aus react-events.md.
 *  - Auto-close on location-change (path A) — siehe Sidebar.tsx. Auto-close
 *    on nav-click (path B) — onClick-handler im Sidebar nav-Link.
 *
 * Flex-Chain (Desktop):
 *   <div flex min-h-screen>
 *     <Sidebar/>                    (240px, position:static)
 *     <main flex-1 flex flex-col overflow-auto>
 *       <Topbar/>                   (sticky in flow, z-30)
 *       <div flex-1 flex flex-col>
 *         {children}
 *       </div>
 *     </main>
 *   </div>
 *
 * Flex-Chain (Mobile):
 *   <div flex min-h-screen>
 *     <Sidebar/>                    (280px, position:fixed z-50, transform-driven)
 *     <Backdrop/>                   (fixed inset-0 z-40, only when sidebarOpen)
 *     <main flex-1 ...>             (overflow-hidden when sidebarOpen)
 *       <Topbar/>                   (sticky z-30; visible behind Backdrop only when open)
 *       <div flex-1 flex flex-col>{children}</div>
 *     </main>
 *   </div>
 */
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'wouter';
import { LayoutContext, ScrollContainerContext, type LayoutApi } from '../lib/layout-context';
import { getSidebarCollapsed, setSidebarCollapsed } from '../lib/sidebar-collapsed';
import { useUploadView } from '../lib/upload-context';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { UploadProvider } from './UploadProvider';
import { UploadToast } from './UploadToast';

// Upload-Modal-VIEW (schwer: Dropzone/FacetPicker/RecipePicker/Queue/Rename)
// bleibt lazy + <Suspense fallback={null}> (Entry-Bundle-Optimierung Session 3,
// Spec-R1-Punkt 6). Die Engine selbst lebt im eager-importierten UploadProvider.
// UploadToast ist klein → eager (kein Suspense-Flacker beim Minimieren).
const UploadModal = lazy(() =>
  import('./UploadModal').then((m) => ({ default: m.UploadModal })),
);

/** Rendert die aktive Upload-View je nach Provider-`view`. Lebt INNERHALB des
 *  UploadProvider (kann den Control-Context konsumieren). Hängt nur am `view`-Enum
 *  → re-rendert nicht pro Progress-Tick (MH 10). */
function UploadViews() {
  const view = useUploadView();
  if (view === 'modal') {
    return (
      <Suspense fallback={null}>
        <UploadModal />
      </Suspense>
    );
  }
  if (view === 'toast') return <UploadToast />;
  return null;
}

type Props = {
  user: { email: string; displayName: string; role: string; isAdmin: boolean };
  onLogout: () => void;
  children: ReactNode;
};

const DESKTOP_MQ = '(min-width: 768px)';

export function Layout({ user, onLogout, children }: Props) {
  const [sidebarOpen, setSidebarOpenState] = useState(false);
  // Desktop-Collapse (Icon-Rail): lazy aus localStorage initialisiert, damit
  // die gemerkte Präferenz OHNE Flash-of-full-Sidebar greift. Nur Desktop-
  // relevant; der Mobile-Drawer liest den Wert nicht (alle Collapse-Styles
  // sind `md:`-gated). 2026-07-19.
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(getSidebarCollapsed);
  const [location] = useLocation();

  // Upload-Flow lebt seit 2026-06-16 im <UploadProvider> (Engine + State + XHRs,
  // damit Minimieren-zu-Toast + SPA-Nav den Upload nicht abbrechen). Der frühere
  // uploadModalOpen/uploadRecipe-State + useUploadModal sind hierher gewandert.

  // Refs für Drawer-Focus-Management (Codex A2-fix). Topbar-hamburger und
  // Sidebar-first-nav-link registrieren sich hier; useEffects unten lesen.
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);
  const firstNavLinkRef = useRef<HTMLAnchorElement | null>(null);
  // App-Scroll-Container (<main>) → via ScrollContainerContext an den Library-
  // Infinite-Scroll-Hook + Virtualizer (getScrollElement).
  const mainScrollRef = useRef<HTMLElement | null>(null);

  // Stable wrapper damit setSidebarOpen-Identity über renders konstant ist
  // (verhindert unnötige useEffect-runs in Konsumenten).
  const setSidebarOpen = useCallback((open: boolean) => {
    setSidebarOpenState(open);
  }, []);

  // Desktop-Collapse-Toggle: State flippen + defensiv persistieren. Stable
  // identity (useCallback) — der Toggle-Button re-rendert nicht die Welt.
  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsedState((prev) => {
      const next = !prev;
      setSidebarCollapsed(next);
      return next;
    });
  }, []);

  // matchMedia: Auto-close drawer bei viewport ≥md (Codex tailwind.md §
  // Body-Scroll-Lock-Sync — verhindert state/DOM-drift bei Browser-resize
  // oder Tablet-Rotation Portrait→Landscape).
  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_MQ);
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setSidebarOpenState(false);
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Auto-close drawer on route-change (Mobile only — auf Desktop irrelevant
  // weil Drawer eh nicht open ist). Codex CO1 Path A.
  useEffect(() => {
    if (!window.matchMedia(DESKTOP_MQ).matches) {
      setSidebarOpenState(false);
    }
  }, [location]);

  // Esc schließt Drawer. Dialog-Guard skipt nur role="dialog"/alertdialog —
  // Menus regulieren sich via stopPropagation (Codex CO4-fix). Pattern aus
  // react-events.md § Window-Keyboard-Shortcut Dialog-State-Aware Guard.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!sidebarOpen) return;
      const dialogOpen = document.querySelector('[role="dialog"], [role="alertdialog"]');
      if (dialogOpen) return;
      setSidebarOpenState(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sidebarOpen]);

  // Body-Scroll-Lock wenn Drawer offen. Aktueller Scroll-Container ist <main>
  // (overflow-auto h-screen). Wir locken BODY (defense-in-depth, falls UA
  // scroll-bubbles) UND <main> via class-toggle (siehe rendered className
  // unten). Codex CO2/CO5: vorherigen body-overflow capturen + restoren statt
  // blind '' setzen.
  useEffect(() => {
    if (!sidebarOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [sidebarOpen]);

  // Drawer-Focus-Management (Codex A2):
  //  - on open: focus first enabled nav-link (mit setTimeout-0 damit
  //    translate-Animation startet bevor focus springt).
  //  - on close: restore focus to hamburger-button.
  // Hamburger ist md:hidden — auf Desktop kein Crash weil .focus() auf
  // hidden element ist no-op.
  const prevOpenRef = useRef(sidebarOpen);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = sidebarOpen;
    if (sidebarOpen && !wasOpen) {
      // open transition
      const id = window.setTimeout(() => {
        firstNavLinkRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
    if (!sidebarOpen && wasOpen) {
      // close transition
      hamburgerRef.current?.focus();
    }
  }, [sidebarOpen]);

  const api: LayoutApi = {
    user,
    sidebarOpen,
    setSidebarOpen,
    sidebarCollapsed,
    toggleSidebarCollapsed,
    hamburgerRef,
    firstNavLinkRef,
  };

  return (
    <UploadProvider>
    <LayoutContext.Provider value={api}>
      <ScrollContainerContext.Provider value={mainScrollRef}>
      <div className="flex min-h-screen bg-white">
        {/* Skip-Link (Sprint UI-D1) — erstes fokussierbares Element, sr-only
            bis Tastatur-Fokus. onClick fokussiert #main-content explizit:
            robust gegen Browser, die einem In-Page-Anker-Klick keinen Fokus
            folgen lassen, und macht das Verhalten in jsdom testbar.
            focus:fixed → kein positionierter Vorfahre nötig. */}
        <a
          href="#main-content"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById('main-content')?.focus();
          }}
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-brand-green-700 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-brand-cream focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:ring-offset-2"
        >
          Zum Inhalt springen
        </a>
        <Sidebar user={user} onLogout={onLogout} />
        {/* Backdrop — only mobile, only when open. Click closes. */}
        {sidebarOpen && (
          <button
            type="button"
            aria-label="Menü schließen"
            onClick={() => setSidebarOpenState(false)}
            className="fixed inset-0 z-40 bg-brand-green-900/40 md:hidden"
          />
        )}
        {/* min-w-0: <main> ist Flex-Item der äußeren flex-row. Ohne min-w-0
            gilt min-width:auto → <main> schrumpft NICHT unter die Content-
            Mindestbreite. Eine horizontal scrollbare Toolbar (FilterBar,
            nowrap + shrink-0) würde <main> sonst breiter als den Viewport
            ziehen statt selbst zu scrollen. */}
        <main
          id="main-content"
          ref={mainScrollRef}
          tabIndex={-1}
          className={`flex-1 min-w-0 flex flex-col h-screen outline-none ${
            sidebarOpen ? 'overflow-hidden' : 'overflow-auto'
          }`}
        >
          <Topbar />
          {/* App-weiter Content-Container: einheitliche max-w-[1320px], zentriert.
              Ersetzt die früheren per-Page-Breiten (full-width vs 1100 vs 1320 →
              uneinheitlich). Bewusst schmale Detail/Form-Seiten (AssetDetail,
              Profile, Vocab) overriden mit ihrem eigenen, kleineren max-w. */}
          <div className="flex-1 flex flex-col w-full max-w-[1320px] mx-auto">{children}</div>
        </main>
      </div>
      {/* Upload-Flow als Provider-View: Modal (view==='modal', lazy) ODER Toast
          (view==='toast', eager). State + XHRs leben im <UploadProvider> oben. */}
      <UploadViews />
      </ScrollContainerContext.Provider>
    </LayoutContext.Provider>
    </UploadProvider>
  );
}
